import { useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { DigitalSignatureField } from "@smart/bridge-intake-components-dom";
import { SignatureMethod } from "@smart/bridge-types-basic";
import { Button, SegmentedControl } from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";

import { ChargeableBadge } from "./chargeable-badge";
import { SignatureTypeModal } from "./signature-type-modal";
import {
  getSignatureTypeAvailability,
  resolveSignatureMethod,
  setDefaultSignatureMethod,
} from "./utils";
import { GqlFieldValues, OnUpdateField } from "../../types";

type SignatureProps = {
  field: GqlFieldValues;
  onUpdateField: OnUpdateField;
  hasSubmissionLinks?: boolean;
};

export const Signature = ({
  field,
  onUpdateField,
  hasSubmissionLinks,
}: SignatureProps) => {
  const providerInfo = useProviderInfo();
  const isTriConvey = providerInfo?.label === "triConvey";
  const providerLabel = isTriConvey ? "triSearch" : "InfoTrack";
  const {
    "LINK-3588": digitalSignatureType,
    "LINK-3846": standardSignatureDisabled,
  } = useFeatureFlags();
  const signatureTypes = getSignatureTypeAvailability({
    digitalSignatureEnabled: digitalSignatureType,
    standardSignatureDisabled,
  });
  const canChangeSignatureType =
    signatureTypes.standard && signatureTypes.digital;
  const method: SignatureMethod = digitalSignatureType
    ? resolveSignatureMethod(field.signature?.method)
    : "electronic";
  const isDigital = method === "digital";
  const [typeModalOpen, setTypeModalOpen] = useState(false);

  const handleConfirm = async (
    nextMethod: SignatureMethod,
    setAsDefault: boolean,
  ) => {
    if (setAsDefault) {
      setDefaultSignatureMethod(nextMethod);
    }
    if (nextMethod !== method) {
      await onUpdateField({
        field,
        updated: { signature: { method: nextMethod } },
      });
    }
    setTypeModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-medium">
      {digitalSignatureType && (
        <div className="flex items-center">
          <div
            className="w-fit rounded px-[0.8rem] py-[0.2rem] text-caption font-semibold text-neutral-850"
            data-testid="signature-method-banner"
          >
            {isDigital ? "Verified Signature" : "Standard Signature"}
          </div>
          <span className="ml-6 mr-3 text-caption text-neutral-500">
            {isDigital
              ? `Powered by ${providerLabel}`
              : "Handwritten or typed signature. No per-use fee."}
          </span>
          {isDigital && (
            <span className="ml-6 mr-3">
              <ChargeableBadge withLogo={false} />
            </span>
          )}
          {canChangeSignatureType && (
            <Button
              variant="secondarySubtle"
              size="small"
              leftIcon="regularPencil"
              onClick={() => setTypeModalOpen(true)}
              dataTestId="change-signature-type"
              title="Change signature type"
            />
          )}
        </div>
      )}
      {!isDigital && (
        <div className="flex">
          <SegmentedControl
            defaultIndex={-1}
            items={[
              { label: "Draw", disabled: true },
              { label: "Type", disabled: true },
            ]}
          />
        </div>
      )}
      {isDigital ? (
        <DigitalSignatureField variant={isTriConvey ? "trisign" : "signit"} />
      ) : (
        <div className="border border-solid border-neutral-200 rounded bg-neutral-20 h-[15rem]" />
      )}

      <SignatureTypeModal
        open={typeModalOpen}
        initialMethod={method}
        onClose={() => setTypeModalOpen(false)}
        onConfirm={handleConfirm}
        digitalDisabled={hasSubmissionLinks}
        standardSignatureDisabled={standardSignatureDisabled}
      />
    </div>
  );
};
